/* eslint-disable @typescript-eslint/no-restricted-imports */
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller, useWatch } from 'react-hook-form';

import Box from '~/common/components/Box';
import Button from '~/common/components/Button';
import FormControl from '~/common/components/Forms/FormControl';
import FormLabel from '~/common/components/Forms/FormLabel';
import CurrencyInput from '~/common/components/Inputs/CurrencyInput';
import Grid from '~/common/mui/Grids/Grid';
import GridItem from '~/common/mui/Grids/GridItem';
import FormBuilderErrorMessage from '~/pages/Loan1003DataGrids/common/components/FormBuilder/FormBuilderErrorMessage';
import InputMapper from '~/pages/Loan1003DataGrids/common/components/FormBuilder/InputMapper';

const BORROWER_AMOUNT_FIELD = 'assuranceCompletionAmount';
const SELLER_AMOUNT_FIELD = 'sellerEscrowHoldbackAmount';

const EITHER_AMOUNT_REQUIRED_MESSAGE =
  'Enter a value in either Borrower Escrow Holdback Amount or Seller Escrow Holdback Amount.';
const AMOUNT_REQUIRED_MESSAGE = 'Required Field';

const hasAmount = (value: unknown) =>
  value !== null && value !== undefined && value !== '';

/**
 * A cleared currency field must stay blank rather than collapsing to `0`, so an
 * empty amount is persisted as `null` instead of an unintended `$0.00`.
 */
const toAmountOrNull = (rawValue: string) => {
  const normalizedValue = String(rawValue ?? '').replaceAll(',', '');
  if (normalizedValue === '') {
    return null;
  }
  const numericValue = Number(normalizedValue);
  return Number.isNaN(numericValue) ? null : numericValue;
};

type EscrowHoldbackFieldsProps = Readonly<{
  control: Control<Record<string, unknown>>;
  getValues: (...args: any[]) => any;
  errors: FieldErrors;
  showFields: boolean;
  isRefinance: boolean;
  onAdd: () => void;
  onRemove: () => void;
  onChangeHandler: (field: string, value: unknown) => void;
  trigger: (...args: any[]) => any;
  isWriteAccessDisabled?: boolean;
}>;

export default function EscrowHoldbackFields({
  control,
  getValues,
  errors,
  showFields,
  isRefinance,
  onAdd,
  onRemove,
  onChangeHandler,
  trigger,
  isWriteAccessDisabled = false,
}: EscrowHoldbackFieldsProps) {
  const assuranceCompletionAmount = useWatch({
    control,
    name: BORROWER_AMOUNT_FIELD,
  });
  const sellerEscrowHoldbackAmount = useWatch({
    control,
    name: SELLER_AMOUNT_FIELD,
  });
  if (!showFields) {
    if (isWriteAccessDisabled) {
      return null;
    }
    return (
      <Box mb={4}>
        <Button
          isPrimary={false}
          size="sm"
          onClick={onAdd}
          data-testid="add-escrow-holdback-btn"
        >
          + Add Escrow Holdback
        </Button>
      </Box>
    );
  }

  const readOnlySx = isWriteAccessDisabled
    ? { opacity: 0.6, cursor: 'not-allowed', '& *': { pointerEvents: 'none' } }
    : {};
  const inputTabIndex = isWriteAccessDisabled ? -1 : 0;

  const isSellerAmountVisible = !isRefinance;
  const amountFieldNames = isSellerAmountVisible
    ? [BORROWER_AMOUNT_FIELD, SELLER_AMOUNT_FIELD]
    : [BORROWER_AMOUNT_FIELD];

  const borrowerAmountError = errors?.[BORROWER_AMOUNT_FIELD];
  const sellerAmountError = errors?.[SELLER_AMOUNT_FIELD];

  const validateAmounts = () => {
    if (hasAmount(getValues(BORROWER_AMOUNT_FIELD))) {
      return true;
    }
    if (!isSellerAmountVisible) {
      return AMOUNT_REQUIRED_MESSAGE;
    }
    return hasAmount(getValues(SELLER_AMOUNT_FIELD))
      ? true
      : EITHER_AMOUNT_REQUIRED_MESSAGE;
  };

  const handleAmountChange = (
    field: string,
    rawValue: string,
    onChange: (value: unknown) => void,
  ) => {
    const amount = toAmountOrNull(rawValue);
    onChange(amount);
    onChangeHandler(field, amount);
    if (borrowerAmountError || sellerAmountError) {
      trigger(amountFieldNames);
    }
  };

  const repairsCompletionDateField = {
    type: 'datepicker',
    name: 'expectedRepairsCompletionDate',
    label: 'Escrow Repair Completed Date',
    dataTestId: 'expectedRepairsCompletionDate',
    disabled: true,
  };

  const repairConditionsField = {
    type: 'textarea',
    name: 'requiredImprovements',
    label: 'Repair Conditions',
    dataTestId: 'requiredImprovements',
    placeholder: 'Repair Conditions',
    disabled: isWriteAccessDisabled,
    onChangeHandler: (e: React.ChangeEvent<HTMLTextAreaElement>) =>
      onChangeHandler('requiredImprovements', e.target.value),
  };

  const renderAmountField = (
    field: string,
    label: string,
    value: unknown,
    error: { message?: string } | undefined,
  ) => (
    <GridItem sx={{ width: '100%', ...readOnlySx }}>
      <Controller
        name={field}
        control={control}
        rules={{ validate: validateAmounts }}
        render={({ onChange, onBlur, name }) => (
          <FormControl>
            <FormLabel htmlFor={name}>{label}</FormLabel>
            <CurrencyInput
              id={name}
              data-testid={field}
              isInvalid={!!error}
              isDisabled={isWriteAccessDisabled}
              tabIndex={inputTabIndex}
              value={(value as string | number | null) ?? ''}
              placeholder={label}
              onBlur={onBlur}
              onChange={(rawValue) =>
                handleAmountChange(field, rawValue, onChange)
              }
            />
          </FormControl>
        )}
      />
      {error && <FormBuilderErrorMessage error={error} />}
    </GridItem>
  );

  return (
    <>
      <Grid
        sx={{
          marginBottom: '1.5rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          rowGap: '1rem',
        }}
      >
        <GridItem
          sx={{
            width: '100%',
            cursor: 'not-allowed',
            '& *': { pointerEvents: 'none' },
            ...readOnlySx,
          }}
        >
          <InputMapper
            {...repairsCompletionDateField}
            tabIndex={inputTabIndex}
            getValues={getValues}
            control={control}
          />
        </GridItem>
        {renderAmountField(
          BORROWER_AMOUNT_FIELD,
          'Borrower Escrow Holdback Amount',
          assuranceCompletionAmount,
          borrowerAmountError,
        )}
        {isSellerAmountVisible &&
          renderAmountField(
            SELLER_AMOUNT_FIELD,
            'Seller Escrow Holdback Amount',
            sellerEscrowHoldbackAmount,
            sellerAmountError,
          )}
        <GridItem sx={{ width: '100%', gridColumn: 'span 3', ...readOnlySx }}>
          <InputMapper
            {...repairConditionsField}
            tabIndex={inputTabIndex}
            getValues={getValues}
            control={control}
          />
        </GridItem>
      </Grid>
      {!isWriteAccessDisabled && (
        <Box mt={2}>
          <Button
            isPrimary={false}
            size="sm"
            onClick={onRemove}
            data-testid="remove-escrow-holdback-btn"
          >
            <FontAwesomeIcon icon={faTrash} style={{ marginRight: '0.5rem' }} />
            Remove Escrow Holdback
          </Button>
        </Box>
      )}
    </>
  );
}
